import styled from '@emotion/styled';
import { Checkbox, Divider } from 'antd';
import FormField from 'components/FormField';
import useSmartField from 'hooks/useSmartField';
import useTrackingRef from 'hooks/useTrackingRef';
import { uniqBy } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import sort from 'utils/sort';
import wait from 'utils/wait';
import { FV, getSlateExecuteContext } from '../share';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 6px;
  > * {
    display: flex;
    align-items: center;
  }
  > span {
    flex: 0 0 auto;
  }
  > div {
    flex: 1 0 0;
    flex-wrap: wrap;
  }
`;

function Regions({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const {
    field: { value: regionExecuteContexts = [], onChange: onRegionExecuteContextsChange },
  } = useSmartField<FV['regionExecuteContexts']>('regionExecuteContexts');

  const regionExecuteContextsRef = useTrackingRef(regionExecuteContexts);

  return (
    <FormField<FV['regions']>
      name="regions"
      label={t('WhatsappBuyers.选择站点')}
      required
      validate={() =>
        wait().then(() =>
          regionExecuteContextsRef.current.length > 0
            ? undefined
            : t('department.请选择x', { x: t('aide.站点') }),
        )
      }
    >
      {({ field: { value = [], onBlur } }) => {
        const checked = (region: General.UpperCaseRegion) =>
          regionExecuteContexts.some(
            ({ enable = true, region: _region }) => enable && region === _region,
          );

        const getRegionContext = (region: General.UpperCaseRegion) => ({
          enable: true,
          region,
          executeContext: getSlateExecuteContext({
            content: '',
            translatedContent: '',
          }),
        });

        const allChecked = value.every(checked);

        return (
          <Container>
            <span>
              <Checkbox
                disabled={disabled}
                checked={allChecked}
                indeterminate={!allChecked && value.some(checked)}
                onChange={({ target: { checked } }) => {
                  onRegionExecuteContextsChange(
                    checked
                      ? sort(
                          uniqBy(
                            [
                              ...regionExecuteContexts.map(({ enable, ...others }) => ({
                                enable: true,
                                ...others,
                              })),
                              ...value.map(getRegionContext),
                            ],
                            'region',
                          ),
                          'region',
                          value,
                        )
                      : regionExecuteContexts.map(({ enable, ...others }) => ({
                          enable: false,
                          ...others,
                        })),
                  );

                  onBlur();
                }}
              >
                {t('feedback.全部')}
              </Checkbox>
              <Divider type="vertical" />
            </span>
            <div>
              {value.map((region) => {
                return (
                  <Checkbox
                    key={region}
                    disabled={disabled}
                    checked={checked(region)}
                    onChange={({ target: { checked } }) => {
                      onRegionExecuteContextsChange(
                        checked
                          ? sort(
                              uniqBy(
                                [
                                  ...regionExecuteContexts.map(({ enable, ...others }) => ({
                                    enable: others.region === region ? true : enable,
                                    ...others,
                                  })),
                                  getRegionContext(region),
                                ],
                                'region',
                              ),
                              'region',
                              value,
                            )
                          : regionExecuteContexts.map((context) => {
                              return context.region !== region
                                ? context
                                : {
                                    ...context,
                                    enable: false,
                                  };
                            }),
                      );

                      onBlur();
                    }}
                  >
                    {getRegionLabel(region)}
                  </Checkbox>
                );
              })}
            </div>
          </Container>
        );
      }}
    </FormField>
  );
}

export default Regions;
